/* =============================================================================
   lp-scripts.js — Beauty by Earth shared LP framework (JS layer)
   -----------------------------------------------------------------------------
   Tiny, dependency-free, idempotent helpers shared across landing pages.
   The "shine" is pure CSS (lp-base.css); this file owns:
     - primary-button LOADING state (`.is-loading` + aria-busy)
     - shade-quiz → product-grid variant `<select>` sync (`bbe:shade-quiz:select`)

   Public API:
     window.BBELP.setLoading(el, isLoading)
     window.BBELP.syncShadeQuizToVariantSelects(detail, scope?)
     window.BBELP.inferShadeQuizTier(text)

   Safe to load more than once: a guard prevents double initialization.
   ========================================================================== */
(function () {
  "use strict";

  if (window.BBELP && window.BBELP._init) return;

  var TIER_HINTS = {
    light: ["light", "fair", "pale", "soft", "fair-to-medium", "light tan", "light-tan", "fair tan"],
    medium: ["medium", "golden", "tan", "warm", "fair-to-medium", "medium-to-dark", "medium tan", "medium-tan", "golden tan"],
    dark: ["dark", "deep", "extra", "bronze", "medium-to-dark", "dark tan", "dark-tan", "deepest", "extra dark"],
  };

  var VARIANT_SELECT =
    "[data-st11-variant-select], .lp-collection-card__select";

  function normalizeText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\s*—\s*sold out\s*$/, "")
      .trim();
  }

  function slugify(value) {
    return normalizeText(value).replace(/\s+/g, "-");
  }

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    return "$" + n.toFixed(2);
  }

  function inferShadeQuizTier(text) {
    var t = normalizeText(text);
    if (!t) return "";
    if (/\bdark\b|\bdeep\b|\bextra\b/.test(t)) return "dark";
    if (/\bmedium\b|\btan\b|\bgolden\b/.test(t)) return "medium";
    if (/\blight\b|\bfair\b|\bpale\b/.test(t)) return "light";
    if (t === "light" || t === "medium" || t === "dark") return t;
    return "";
  }

  function detailFromQuizLightbox(lb) {
    if (!lb) return null;
    var titleEl = lb.querySelector("#lp-shade-quiz-result-title");
    var title = titleEl ? String(titleEl.textContent || "").trim() : "";
    var tier = inferShadeQuizTier(title);
    if (!tier) {
      var tally = { light: 0, medium: 0, dark: 0 };
      lb.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
        var v = normalizeText(input.value);
        if (tally[v] !== undefined) tally[v] += 1;
      });
      var keys = ["light", "medium", "dark"];
      keys.sort(function (a, b) {
        if (tally[b] !== tally[a]) return tally[b] - tally[a];
        var pref = { medium: 0, light: 1, dark: 2 };
        return pref[a] - pref[b];
      });
      tier = keys[0];
    }
    if (!tier) return null;
    return {
      tier: tier,
      title: title || tier.charAt(0).toUpperCase() + tier.slice(1),
      shade: slugify(title || tier),
    };
  }

  function optionLabel(option) {
    if (!option) return "";
    return normalizeText(
      option.getAttribute("data-variant-label") || option.textContent || ""
    );
  }

  function scoreOption(label, detail) {
    var tier = normalizeText(detail.tier);
    var title = normalizeText(detail.title);
    var shade = normalizeText(detail.shade);
    if (!label) return -1;

    if (title && label === title) return 1000;
    if (shade && (label === shade || label.replace(/-/g, " ") === shade.replace(/-/g, " "))) {
      return 900;
    }
    if (tier && label === tier) return 800;

    var hints = TIER_HINTS[tier] || [tier];
    var best = 0;
    for (var i = 0; i < hints.length; i++) {
      var hint = hints[i];
      if (!hint) continue;
      if (label === hint) best = Math.max(best, 700);
      else if (label.indexOf(hint) !== -1) best = Math.max(best, 500 - hint.length);
    }
    return best;
  }

  function optionDisabled(option) {
    return !!(option && option.disabled);
  }

  function matchOptionIndex(select, detail) {
    var bestIdx = -1;
    var bestScore = 0;
    for (var i = 0; i < select.options.length; i++) {
      var opt = select.options[i];
      if (optionDisabled(opt)) continue;
      var score = scoreOption(optionLabel(opt), detail);
      if (score > bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    }
    return bestScore > 0 ? bestIdx : -1;
  }

  function syncCardFromSelect(sel) {
    var card = sel.closest("[data-st11-card]");
    if (!card) return;
    var opt = sel.options[sel.selectedIndex];
    if (!opt) return;
    var price = parseInt(opt.getAttribute("data-price"), 10) || 0;
    var compare = parseInt(opt.getAttribute("data-compare"), 10) || 0;
    card.setAttribute("data-st11-variant", opt.value);
    card.setAttribute("data-st11-price", String(price));
    card.setAttribute("data-st11-compare", String(compare));
    var nowEl = card.querySelector("[data-st11-price-display]");
    if (nowEl) {
      if (card.getAttribute("data-st11-mode") === "upsell") {
        nowEl.textContent = money(price);
      } else if (card.getAttribute("data-st11-mode") === "gift") {
        nowEl.textContent = "$0.00";
      } else {
        nowEl.textContent = money(price);
      }
    }
    var compareEl = card.querySelector("[data-st11-compare-display]");
    if (compareEl) {
      if (card.getAttribute("data-st11-mode") === "upsell") {
        compareEl.textContent = compare > price ? money(compare) : "";
        compareEl.hidden = !(compare > price);
      } else if (card.getAttribute("data-st11-mode") === "gift") {
        compareEl.textContent = money(price);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = compare > price ? money(compare) : "";
        compareEl.hidden = !(compare > price);
      }
    }
    var titleEl = card.querySelector("[data-st11-variant-in-title]");
    if (titleEl && card.getAttribute("data-st11-mode") === "upsell") {
      var label = optionLabel(opt).split(" - ")[0];
      if (label && label !== "default title") {
        titleEl.textContent = " (" + label.charAt(0).toUpperCase() + label.slice(1) + ")";
        titleEl.hidden = false;
      } else {
        titleEl.textContent = "";
        titleEl.hidden = true;
      }
    }
  }

  function syncShadeQuizToVariantSelects(detail, scope) {
    if (!detail) return 0;
    var tier = normalizeText(detail.tier);
    if (!tier && detail.title) tier = inferShadeQuizTier(detail.title);
    if (!tier) return 0;
    detail = {
      tier: tier,
      title: detail.title || "",
      shade: detail.shade || slugify(detail.title || tier),
    };

    var roots = [];
    if (scope && scope.querySelectorAll) {
      roots.push(scope);
    } else {
      document.querySelectorAll("[data-st11-root]").forEach(function (root) {
        roots.push(root);
      });
      if (!roots.length) roots.push(document);
    }

    var synced = 0;
    roots.forEach(function (root) {
      root.querySelectorAll(VARIANT_SELECT).forEach(function (sel) {
        var idx = matchOptionIndex(sel, detail);
        if (idx < 0) return;
        if (sel.selectedIndex !== idx) {
          sel.selectedIndex = idx;
        }
        syncCardFromSelect(sel);
        sel.dispatchEvent(new Event("change", { bubbles: true }));
        synced += 1;
      });
      var shop = root.querySelector("#shop");
      if (shop && synced > 0) {
        try {
          shop.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch (e) {
          shop.scrollIntoView();
        }
      }
    });
    return synced;
  }

  function bindShadeQuizSync() {
    if (document.documentElement.dataset.lpShadeQuizSyncBound === "1") return;
    document.documentElement.dataset.lpShadeQuizSyncBound = "1";

    document.addEventListener("bbe:shade-quiz:select", function (e) {
      syncShadeQuizToVariantSelects((e && e.detail) || {});
    });

    document.addEventListener(
      "click",
      function (e) {
        var btn =
          e.target && e.target.closest
            ? e.target.closest(".js-shade-quiz-select-shade")
            : null;
        if (!btn) return;
        var lb = btn.closest("#lp-shade-quiz-lb");
        var detail = detailFromQuizLightbox(lb);
        if (detail) syncShadeQuizToVariantSelects(detail);
      },
      true
    );

    window.BBEShadeQuiz = window.BBEShadeQuiz || {};
    window.BBEShadeQuiz.onSelect = function (detail) {
      syncShadeQuizToVariantSelects(detail || {});
    };
  }

  function setLoading(el, isLoading) {
    if (!el || !el.classList) return;
    var on = isLoading !== false;
    el.classList.toggle("is-loading", on);
    if (on) {
      el.setAttribute("aria-busy", "true");
    } else {
      el.removeAttribute("aria-busy");
    }
  }

  function onClick(event) {
    var btn = event.currentTarget;
    if (!btn || btn.classList.contains("is-loading")) return;
    if (btn.disabled || event.defaultPrevented) return;
    if (event.button && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    setLoading(btn, true);
  }

  function wire(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var btns = scope.querySelectorAll(".lp-btn-primary[data-lp-loading]");
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (btn.dataset.lpLoadingBound === "1") continue;
      btn.dataset.lpLoadingBound = "1";
      btn.addEventListener("click", onClick);
    }
  }

  function updateShopGridProgress(wrap, visible, total) {
    var progress = wrap.querySelector("[data-lp-shop-progress]");
    if (!progress) return;
    progress.textContent = "Showing " + visible + " of " + total + " products";
  }

  function initShopGridPagination(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var wraps = scope.querySelectorAll("[data-lp-shop-grid]");
    for (var w = 0; w < wraps.length; w++) {
      var wrap = wraps[w];
      if (wrap.dataset.lpShopGridBound === "1") continue;
      wrap.dataset.lpShopGridBound = "1";

      var pageSize = parseInt(wrap.getAttribute("data-lp-shop-page-size"), 10);
      if (!pageSize || pageSize < 1) pageSize = 8;

      var grid = wrap.querySelector(".lp-grid--products");
      if (!grid) continue;

      var cards = grid.querySelectorAll(".lp-collection-card:not(.lp-collection-card--placeholder)");
      var total = cards.length;
      if (total <= pageSize) continue;

      for (var c = pageSize; c < total; c++) {
        cards[c].classList.add("lp-shop-grid__card--hidden");
      }

      var visible = pageSize;
      updateShopGridProgress(wrap, visible, total);

      var loadBtn = wrap.querySelector("[data-lp-shop-load-more]");
      if (!loadBtn) continue;

      loadBtn.addEventListener("click", function () {
        var hidden = grid.querySelectorAll(".lp-collection-card.lp-shop-grid__card--hidden");
        if (!hidden.length) {
          loadBtn.hidden = true;
          return;
        }

        var batch = Math.min(pageSize, hidden.length);
        for (var h = 0; h < batch; h++) {
          hidden[h].classList.remove("lp-shop-grid__card--hidden");
        }

        visible = Math.min(total, visible + batch);
        updateShopGridProgress(wrap, visible, total);

        if (!grid.querySelector(".lp-collection-card.lp-shop-grid__card--hidden")) {
          loadBtn.hidden = true;
        }
      });
    }
  }

  function init() {
    wire(document);
    bindShadeQuizSync();
    initShopGridPagination(document);
  }

  window.BBELP = {
    _init: true,
    setLoading: setLoading,
    wire: wire,
    initShopGridPagination: initShopGridPagination,
    inferShadeQuizTier: inferShadeQuizTier,
    syncShadeQuizToVariantSelects: syncShadeQuizToVariantSelects,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
